import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { SearchIcon, BellIcon, LogoutIcon, MenuIcon, CloseIcon } from "./icons"
import { Drawer } from "./drawer"
import { Modal, ModalActions, ModalButton } from "./modal"

interface HeaderProps {
  name?: string | null
  email?: string | null
  avatarUrl?: string | null
  onLogout: () => void
  onMobileMenuToggle: () => void
  onProfileUpdated: (name: string) => void
}

type NotificationType = "nomination" | "inquiry" | "application" | "school_application" | "form_submission"

interface AdminNotification {
  id: string
  type: NotificationType
  title: string
  body: string
  href: string
  readAt: string | null
  resolvedAt: string | null
  createdAt: string
}

const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  nomination: "Nomination",
  inquiry: "Inquiry",
  application: "Application",
  school_application: "School application",
  form_submission: "Form submission",
}

function formatNotificationTime(value: string): string {
  const diffSeconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000))
  if (diffSeconds < 60) return "Just now"
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`
  if (diffSeconds < 604800) return `${Math.floor(diffSeconds / 86400)}d ago`
  return new Date(value).toLocaleDateString()
}

export function Header({ name, email, avatarUrl, onLogout, onMobileMenuToggle, onProfileUpdated }: HeaderProps) {
  const navigate = useNavigate()
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [notifications, setNotifications] = useState<AdminNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [profileOpen, setProfileOpen] = useState(false)
  const [profileName, setProfileName] = useState("")
  const [savingProfile, setSavingProfile] = useState(false)
  const displayName = name || "Admin"
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()

  const fetchNotifications = async () => {
    try {
      const response = await client.api.admin.notifications.$get({ query: { limit: "20" } })
      const body = await response.json()
      if (response.ok && "data" in body) {
        setNotifications(body.data.items as AdminNotification[])
        setUnreadCount(body.data.unreadCount)
      }
    } catch {
      // Notifications should not block the admin shell if the endpoint is unavailable.
    }
  }

  useEffect(() => {
    void fetchNotifications()
  }, [])

  const markReadAndNavigate = async (notification: AdminNotification) => {
    setNotificationsOpen(false)
    if (!notification.readAt) {
      try {
        await client.api.admin.notifications[":id"].read.$patch({ param: { id: notification.id } })
      } finally {
        setNotifications((items) => items.map((item) => item.id === notification.id ? { ...item, readAt: new Date().toISOString() } : item))
        setUnreadCount((count) => Math.max(0, count - 1))
      }
    }
    navigate(notification.href)
  }

  const markAllRead = async () => {
    try {
      await client.api.admin.notifications["read-all"].$post({})
      setNotifications((items) => items.map((item) => ({ ...item, readAt: item.readAt ?? new Date().toISOString() })))
      setUnreadCount(0)
    } catch {
      // Keep the current state when marking all notifications fails.
    }
  }

  const handleProfileSave = async (event: React.FormEvent) => {
    event.preventDefault()
    const nextName = profileName.trim()
    if (!nextName) return

    setSavingProfile(true)
    try {
      const response = await client.api.admin.auth.me.$patch({ json: { name: nextName } })
      const body = await response.json()
      if (!response.ok || !("data" in body)) {
        toast.error(getErrorMessage(body))
        return
      }
      onProfileUpdated(body.data.name)
      setProfileOpen(false)
      toast.success("Profile updated")
    } catch {
      toast.error("Failed to update profile")
    } finally {
      setSavingProfile(false)
    }
  }

  return (
    <header className="sticky top-0 z-30 flex h-[72px] items-center justify-between border-b border-gold-500/50 bg-cream-50 px-5 lg:px-8">
      <button
        type="button"
        aria-label="Open navigation"
        onClick={onMobileMenuToggle}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-gold-500 bg-white text-ink lg:hidden"
      >
        <MenuIcon className="h-5 w-5" />
      </button>

      <div className="hidden flex-1 lg:block">
        <div className="relative max-w-md">
          <SearchIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink/40" />
          <input
            type="search"
            placeholder="Search..."
            className="h-11 w-full rounded-full border border-ink/10 bg-gold-500/30 pl-12 pr-4 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label="Notifications"
          aria-expanded={notificationsOpen}
          aria-haspopup="dialog"
          onClick={() => {
            setNotificationsOpen((open) => !open)
            void fetchNotifications()
          }}
          className="relative flex h-10 w-10 items-center justify-center rounded-full border border-gold-500 bg-white text-ink transition-colors hover:bg-cream-100"
        >
          <BellIcon className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        <Drawer variant="light" open={notificationsOpen} onClose={() => setNotificationsOpen(false)}>
          <header className="flex items-start justify-between border-b border-gold-500/50 px-5 py-5 sm:px-6">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight text-ink">Notifications</h2>
              <p className="mt-1 text-sm text-ink/50">
                {unreadCount > 0 ? `${unreadCount} unread` : "You're all caught up"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => void markAllRead()}
                  className="text-xs font-semibold text-forest-700 transition-colors hover:text-forest-950"
                >
                  Mark all read
                </button>
              )}
              <button
                type="button"
                aria-label="Close notifications"
                onClick={() => setNotificationsOpen(false)}
                className="rounded-md p-1.5 text-ink/50 transition-colors hover:bg-cream-100 hover:text-ink"
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
          </header>

          <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            {notifications.length === 0 ? (
              <p className="py-16 text-center text-sm text-ink/50">You&apos;re all caught up.</p>
            ) : (
              <div className="space-y-2">
                {notifications.map((notification) => (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() => void markReadAndNavigate(notification)}
                    className={`flex w-full gap-3 rounded-[10px] border border-gold-500/50 px-4 py-4 text-left transition-colors hover:bg-cream-100 ${notification.readAt ? "bg-white" : "bg-cream-50"}`}
                  >
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${notification.readAt ? "bg-ink/20" : "bg-forest-700"}`} aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-3">
                        <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-forest-700">
                          {NOTIFICATION_TYPE_LABELS[notification.type]}
                        </span>
                        <span className="shrink-0 text-[11px] text-ink/40">{formatNotificationTime(notification.createdAt)}</span>
                      </span>
                      <span className="mt-1 block text-sm font-semibold text-ink">{notification.title}</span>
                      <span className="mt-1 block text-xs leading-5 text-ink/60">{notification.body}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Drawer>

        <button
          type="button"
          aria-label="Edit profile"
          onClick={() => {
            setProfileName(displayName)
            setProfileOpen(true)
          }}
          className="flex items-center gap-2 rounded-full border border-gold-500 bg-white py-1.5 pl-1.5 pr-4 transition-colors hover:bg-cream-100"
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-forest-950 text-xs font-bold text-white">
              {initials}
            </span>
          )}
          <span className="hidden text-sm font-medium text-ink sm:block">{displayName}</span>
        </button>

        <button
          type="button"
          aria-label="Sign out"
          onClick={onLogout}
          className="flex h-10 items-center gap-2 rounded-full border border-ink/20 bg-white px-4 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
        >
          <LogoutIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Sign out</span>
        </button>
      </div>

      <Modal
        isOpen={profileOpen}
        onClose={() => {
          if (!savingProfile) setProfileOpen(false)
        }}
        title="Edit profile"
        subtitle="Update the name shown in the admin workspace."
        maxWidth="sm"
      >
        <form onSubmit={handleProfileSave} className="space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink">Name</span>
            <input
              type="text"
              required
              autoFocus
              value={profileName}
              onChange={(event) => setProfileName(event.target.value)}
              className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink focus:border-forest-700 focus:outline-none"
            />
          </label>
          <div>
            <p className="mb-1.5 text-sm font-medium text-ink">Email</p>
            <p className="rounded-lg bg-cream-100 px-4 py-2.5 text-sm text-ink/60">{email || "Admin account"}</p>
          </div>
          <ModalActions>
            <ModalButton onClick={() => setProfileOpen(false)} disabled={savingProfile}>Cancel</ModalButton>
            <ModalButton type="submit" variant="primary" disabled={savingProfile || !profileName.trim()}>
              {savingProfile ? "Saving..." : "Save changes"}
            </ModalButton>
          </ModalActions>
        </form>
      </Modal>
    </header>
  )
}
